

import axios from "axios";
import React, { useEffect, useState, useContext } from "react";
import { FaCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { CommonContext } from "../../App";
import SubGroupsTable from "../TypesAndGroups/Ladgers/SubGroupsTable";
import LedgerTables from "../TypesAndGroups/Ladgers/LedgerTables";
import { SiGoogletagmanager } from "react-icons/si";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "../../Components/CommonComponent/loadingSpiner/LoadingSpinner";
import DateFilter from "../../Components/CommonComponent/Dropdown/DateFilter";
import api from "../../services/api";

export default function TrialBalance() {
  const { t } = useTranslation();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [ledger, setLedger] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const storedLang = JSON.parse(localStorage.getItem("second_language"));
  const language = storedLang?.language || "en";

  const navigate = useNavigate();

  const today = new Date();
  const formatDate = (date) => date.toISOString().split("T")[0];
  const currentYear = today.getFullYear();
  const aprilFirst = new Date(`${currentYear}-04-01`);
  const startYear = today >= aprilFirst ? currentYear : currentYear - 1;

  const { branchId, dateRange, setDateRange } = useContext(CommonContext);
  const [startDate, setStartDate] = useState(dateRange.startDate);
  const [endDate, setEndDate] = useState(dateRange.endDate);

  useEffect(() => {
    setStartDate(dateRange.startDate);
    setEndDate(dateRange.endDate);
  }, [dateRange]);

  // --- Fetch branch-wise Trial Balance ---
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`ledgers/get-trial-summary`, {
          params: { startDate, endDate, branchId },
        });

        if (res.data.success && Array.isArray(res.data.data)) {
          const branchesData = res.data.data.map((branch) => {
            // const rows =
            //   branch.branchData?.map((item) => {
            //     const debit =
            //       item.totalReceived > item.totalPaid
            //         ? item.totalReceived - item.totalPaid
            //         : 0;
            //     const credit =
            //       item.totalPaid > item.totalReceived
            //         ? item.totalPaid - item.totalReceived
            //         : 0;
            //     return { ...item, debit, credit };
            //   }) || [];

            const rows =
              branch.branchData?.map((item) => {
                // Opening effect
                const openingEffect =
                  item.openingBalanceType === "received"
                    ? item.openingBalance
                    : -item.openingBalance;

                // Current movement
                const movement =
                  (item.totalReceived || 0) - (item.totalPaid || 0);

                // Final Net Balance
                const net = openingEffect + movement;

                const debit = net > 0 ? net : 0;
                const credit = net < 0 ? Math.abs(net) : 0;

                return { ...item, debit, credit };
              }) || [];

            return {
              branchId: branch.branchId,
              branchName: branch.branchName,
              rows,
            };
          });

          setBranches(branchesData);
        } else {
          setBranches([]);
        }
      } catch (err) {
        console.error("Error fetching trial balance:", err);
        setError("Error fetching trial balance");
        setBranches([]);
      } finally {
        setLoading(false);
      }
    };
    if (startDate && endDate && branchId) {
      fetchData();
    }
  }, [startDate, endDate, branchId]);

  /** Group Data */
  const structureData = (rows) => {
    const grouped = {};
    const ungrouped = [];

    rows.forEach((row) => {
      const { groupType, groupName, groupNameInHindi, subGroupName, subGroupNameInHindi, debit, credit } = row;

      if (!groupName && !subGroupName) {
        ungrouped.push(row);
        return;
      }

      if (!grouped[groupName]) {
        grouped[groupName] = {
          groupType,
          groupName,
          groupNameInHindi,
          subGroupName,
          subGroupNameInHindi,
          subGroups: {},
          totalDebit: 0,
          totalCredit: 0,
        };
      }

      if (subGroupName) {
        if (!grouped[groupName].subGroups[subGroupName]) {
          grouped[groupName].subGroups[subGroupName] = {
            debit: 0,
            credit: 0,
            subGroupNameInHindi
          };
        }
        grouped[groupName].subGroups[subGroupName].debit += debit;
        grouped[groupName].subGroups[subGroupName].credit += credit;
      }

      grouped[groupName].totalDebit += debit;
      grouped[groupName].totalCredit += credit;
    });

    return { grouped, ungrouped };
  };

  const toggleGroup = (branchId, groupName) => {
    const key = `${branchId}-${groupName}`;
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className=" mt-2 bg-white shadow-sm rounded-1 p-2">

      <div className="flex justify-between border-b bg-theme-headerBg text-theme-color mb-1 py-1 px-2 rounded-1">
        <div className=" font-medium mt-1">{t("trialBalance")}</div>
        <div className="text-theme-headerBg">
          <DateFilter
            storageKey="trialBalance"
            customMode="range"
            availableFilters={["default", "today", "custom"]}
            onChange={(r) => setDateRange({
              startDate: r.startDate,
              endDate: r.endDate
            })} />
        </div>
      </div>


      {loading && <div className="text-center py-4"> <LoadingSpinner size={70} color="#000000" /></div>}


      {!loading && branches.length === 0 && (
        <div className="text-center text-gray-500 py-10 text-sm">
          {t("noDataAvailable")}
        </div>
      )}

      {/* All Branches */}
      {!loading &&
        branches.map((branch) => {
          const rows = branch.rows;

          return (
            <div key={branch.branchId} className="mb-2 ">
              {/* Branch Title Always Visible */}
              <div className=" font-medium mb-2  text-theme-headerBg flex justify-center gap-1 font-serif px-1">

                <div className="capitalize border-b border-theme-sidebarBg">{branch.branchName}</div>
              </div>

              {/* ❗ Show "No data found" when rows = 0 */}
              {rows.length === 0 ? (
                <div className="text-center text-gray-400  text-sm py-2">
                  {t("noDataAvailable")}
                </div>
              ) : (
                <>
                  {/* Table */}
                  <table className="w-full border-collapse rounded-lg mt-2">
                    <thead>
                      <tr className="border-b text-sm bg-theme-headerBg text-theme-color">
                        <th className="px-1 font-semibold py-[2px] text-left">{t("ledger/account")}</th>
                        <th className="px-1 font-semibold  text-right">{t("paid")} (₹)</th>
                        <th className="px-1 font-semibold  text-right">{t("received")} (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const { grouped, ungrouped } = structureData(rows);

                        const totalDebit = rows.reduce(
                          (sum, r) => sum + r.debit,
                          0
                        );
                        const totalCredit = rows.reduce(
                          (sum, r) => sum + r.credit,
                          0
                        );

                        return (
                          <>
                            {/* Groups */}
                            {Object.entries(grouped).map(
                              ([groupName, groupData]) => {
                                const hasSub =
                                  Object.keys(groupData.subGroups).length > 0;
                                const key = `${branch.branchId}-${groupName}`;
                                const isExpanded = expandedGroups[key];

                                return (
                                  <React.Fragment key={groupName}>
                                    <tr
                                      className={`border-b bg-theme-tableBg cursor-pointer text-sm ${hasSub
                                          ? "cursor-pointer hover:bg-theme-tableHoverColor"
                                          : ""
                                        }`}
                                      onClick={() =>
                                        hasSub &&
                                        toggleGroup(branch.branchId, groupName)
                                      }
                                    >
                                      <td className="py-[2px] px-1 flex items-center">
                                        {groupName} {language !== "en" && (groupData.groupNameInHindi)}
                                        {hasSub && (
                                          <span className="ml-2 text-[9.5px]">
                                            {isExpanded ? "▲" : "▼"}
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-1 text-right">
                                        {groupData.totalDebit.toLocaleString(
                                          "en-IN"
                                        )}
                                      </td>
                                      <td className="px-1 text-right">
                                        {groupData.totalCredit.toLocaleString(
                                          "en-IN"
                                        )}
                                      </td>
                                    </tr>

                                    {/* Subgroups */}
                                    {hasSub &&
                                      isExpanded &&
                                      Object.entries(groupData.subGroups).map(
                                        ([subName, vals]) => (
                                          <tr
                                            key={subName}
                                            className="border-b bg-theme-tableBg hover:bg-theme-tableHoverColor text-sm"
                                          >
                                            <td
                                              className="py-[2px] ps-5 flex items-center cursor-pointer gap-1"
                                              onClick={subName === 'difference in opening balance' ? undefined:() =>
                                                navigate(`/account-books/ledgers-table`, {
                                                  state: {
                                                    ledger: groupData,
                                                    btnHeading: "Trial Balance",
                                                    btnlink:
                                                      "/reports/trial-balance",
                                                  },
                                                })
                                              }
                                            >
                                              <FaCircle size={7} />
                                              {subName} {language !== "en" && (vals.subGroupNameInHindi)}
                                            </td>
                                            <td className="px-1 text-right">
                                              {vals.debit.toLocaleString(
                                                "en-IN"
                                              )}
                                            </td>
                                            <td className="px-1 text-right">
                                              {vals.credit.toLocaleString(
                                                "en-IN"
                                              )}
                                            </td>
                                          </tr>
                                        )
                                      )}
                                  </React.Fragment>
                                );
                              }
                            )}

                            {/* Ungrouped */}
                            {ungrouped.map((item) => (
                              <tr key={item.id || item.name} className="border-b bg-theme-tableBg hover:bg-theme-tableHoverColor text-sm">
                                <td className="py-[2px] px-1 ">{item.name} {language !== "en" && (item.nameInHindi)}</td>
                                <td className="px-1 text-right">
                                  {item.debit.toLocaleString("en-IN")}
                                </td>
                                <td className="px-1 text-right">
                                  {item.credit.toLocaleString("en-IN")}
                                </td>
                              </tr>
                            ))}

                            {/* totals */}
                            <tr
                              className={`font-bold  border-theme-headerBg text-sm ${totalDebit.toFixed(2) === totalCredit.toFixed(2)
                                  ? "text-green-700"
                                  : "text-red-600"
                                }`}
                            >
                              <td className="px-1 border-b pt-2">{t("total")}</td>
                              <td className="px-1 border-b pt-2 text-right">
                                {totalDebit.toLocaleString("en-IN")}
                              </td>
                              <td className="px-1 border-b pt-2 text-right">
                                {totalCredit.toLocaleString("en-IN")}
                              </td>
                            </tr>
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          );
        })}

      {/* Modal */}
      {modalVisible && (
        <div className="fixed inset-0 bg-black/20 flex justify-center items-center z-50">
          <div
            className="rounded max-w-5xl max-h-[70vh] w-full shadow-lg overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <SubGroupsTable ledger={ledger} setLedger={setLedger} />
            <LedgerTables ledger={ledger} setLedger={setLedger} />
          </div>
        </div>
      )}
    </div>
  );
}
